import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";
import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "How Alrannan Administrative Services automated sales with Gallabox",
        sector: "Service",
        externalLink: "/case-study/alrannan-administrative-services",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/goireland.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/goireland",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        sector: "Service",
        externalLink: "/case-study/dubai-housekeeping",
    },
];

const BlueguardMiddleEastFirstAidTraining: React.FC = () => {
    return (
        <NewLayout
            title="Blueguard Middle East | WhatsApp API Success Story"
            description="Discover how Blueguard Middle East (BGME) First Aid Training generated more leads and improved revenue using Gallabox’s WhatsApp automation solutions."
            canonical="https://gallabox.com/case-study/blueguard-middle-east-first-aid-training"
            ogAttributes={{
                image: "https://assets.gallabox.com/gb-home/case-study/blueguard/blueguard-og.png",
            }}
        >
            <CaseStudyHerosection
                title=" How BGME First Aid Training scaled revenue on WhatsApp with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/blueguard/bluegruard-hero-image.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "69%", label: "Increase in business growth" },
                    { percentage: "60%", label: " Higher customer satisfaction" },
                    { percentage: "60%", label: "Growth in lead generation" },
                ]}
            />

            <section className="max-w-6xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Professional services"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Whatsapp Chatbot" },
                                    { link: "https://gallabox.com/whatsapp-flows", label: "WhatsApp Flows" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                    { link: "https://gallabox.com/whatsapp-shared-inbox", label: "Shared team Inbox" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About">
                            <p className="mt-2">
                                Founded in 2017 by Luke Cunningham, Blueguard Middle East
                                <a href="https://www.blueguardme.com" className="text-blue-500 px-1">
                                    (BGME)
                                </a>
                                has become one of the MENA region&apos;s most trusted providers of professional lifeguard and first aid training.
                            </p>
                            <p className="mt-5">
                                Operating from Dubai and accredited by both Highfield Awarding Body for Compliance (HABC) and the Dubai Corporation of Ambulance (DCAS), BGME
                                delivers British standard training across diverse sectors — from corporate offices and hotels to schools and adventure tourism operators. Their
                                mission is simple yet profound: &ldquo;make everyone a lifesaver&ldquo;.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"The Challenge: Lost in translation between sales and ops"} className="mt-0" />
                            <p className="mt-2">
                                As Saiful Islam from BGME explains, their primary pain point before Gallabox was clear. The lack of a bridge between their messaging interactions
                                and CRM. For a training company managing multiple course types, numerous corporate clients, and thousands of individual certifications, the
                                disconnect between customer communications and their CRM system was creating significant operational friction.
                            </p>
                            <p className="mt-5">
                                Every customer inquiry, booking confirmation, and follow-up would have had to be manually transferred between systems. With courses running daily
                                and certifications requiring renewal every three years, the volume of customer interactions, their support team did most of the heavy-lifting
                                manually. This wasn’t scalable nor sustainable.
                            </p>

                            <CaseStudyInformation subTitle={"Finding the right fit"} className="mt-12" />
                            <p className="mt-2">
                                When evaluating solutions, BGME&apos;s criteria went beyond basic functionality. They needed a platform that could seamlessly connect with their
                                existing CRM while providing the communication features essential for a training business. After considering alternatives, Gallabox stood out for a
                                compelling reason.
                            </p>
                            <p className="mt-5">
                                It wasn’t just a quick pricing win for Gallabox. Their choice was also motivated by the value for money and the price-to-performance and
                                price-to-feature ratios when compared to other WhatsApp automation platforms.. In an industry where efficiency matters most, finding a solution that
                                delivered enterprise-level capabilities at a reasonable price point was crucial.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Strategic implementation for triple growth goals"} className="mt-0" />
                            <p className="mt-2">
                                BGME approached Gallabox with three specific objectives: increase cross-sell and upsell opportunities, improve team productivity, and automate
                                customer support. The implementation leveraged three key features:
                            </p>

                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            "Chatbots handle initial inquiries about course availability and requirements, routing customers to the right training program instantly.",
                                    },
                                    {
                                        listItem:
                                            "Flows guide customers through their entire training journey and automatically trigger renewal reminders as certifications approach expiration.",
                                    },
                                    {
                                        listItem:
                                            " Integrations create seamless data flow between communication channels and their CRM – solving the original pain point that brought BGME to Gallabox.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Direct impact on revenue"} className="mt-0" />
                            <p className="mt-2">
                                The impact of Gallabox on BGME&apos;s business has been nothing short of transformative. Saiful reports a remarkable 69% impact on overall business
                                growth – a figure that reflects not just increased revenue but fundamental improvements in how the company operates.
                            </p>
                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            " Employee productivity increased by 50%: With the support team no longer drowning in manual data entry, trainers and administrators can focus on what they do best – saving lives through education",
                                    },
                                    {
                                        listItem:
                                            " Customer satisfaction rates rose by 60%: Faster response times, accurate information, and proactive renewal reminders have dramatically improved the customer experience",
                                    },
                                    {
                                        listItem:
                                            "Lead generation shot up by 60%: Automated nurturing and better follow-up processes mean more inquiries convert into enrolled students",
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <CaseStudyInformation subTitle={"Where technology meets a life-saving mission"} className="mt-10" />
                            <p className="mt-2">
                                What makes BGME&apos;s success particularly impressive is how these improvements compound. Higher employee productivity means trainers can
                                accommodate more sessions. Better customer satisfaction drives word-of-mouth referrals in Dubai&apos;s tight-knit business community. Increased lead
                                generation fills courses more efficiently, improving profitability.
                            </p>
                            <p className="mt-5">
                                As workplace safety regulations tighten across the UAE and awareness of first aid importance grows, BGME is perfectly positioned to meet increasing
                                demand. The infrastructure provided by Gallabox ensures they can scale without sacrificing the quality that has made them the preferred training
                                partner for organizations from educational institutions to resorts and Hotels to adventure tourism operators.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <div>
                    <h3 className="text-gray-900 max-w-6xl mx-auto mb-10 mt-5 text-center ml-4 md:text-left">Keep exploring stories like this.</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </div>
            </div>
        </NewLayout>
    );
};

export default BlueguardMiddleEastFirstAidTraining;
